
import React, { useEffect, useState, useRef } from 'react';
import { Phone, PhoneOff, User, Zap, MapPin, IndianRupee, ShieldCheck, Sparkles, ChevronUp } from 'lucide-react';
import { JobRequest } from '../types';
import { useTranslation } from '../translations';

interface WorkerIncomingCallScreenProps {
  job: JobRequest;
  onAccept: () => void;
  onDecline: () => void;
}

export const WorkerIncomingCallScreen: React.FC<WorkerIncomingCallScreenProps> = ({ job, onAccept, onDecline }) => {
  const [timer, setTimer] = useState(30);
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeY, setSwipeY] = useState(0);
  const swipeRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const lang = (localStorage.getItem('sl_lang') || 'en') as any;
  const t = useTranslation(lang);

  // Ringtone and Haptic Simulation
  useEffect(() => {
    // Premium ringing sound
    audioRef.current = new Audio('https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3');
    if (audioRef.current) {
      audioRef.current.loop = true;
      audioRef.current.play().catch(e => {
        if (e.name !== 'AbortError' && e.name !== 'NotAllowedError') {
          console.warn("Audio Context blocked", e);
        }
      });
    }

    const countdown = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          clearInterval(countdown);
          onDecline();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(countdown);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [onDecline]);

  // Swipe Logic
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsSwiping(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwiping || !swipeRef.current) return;
    const trackHeight = 160; // match CSS
    const touchY = e.touches[0].clientY;
    const rect = swipeRef.current.getBoundingClientRect();
    const relativeY = rect.bottom - touchY;

    // Clamp between 0 and trackHeight
    const clampedY = Math.max(0, Math.min(trackHeight, relativeY));
    setSwipeY(clampedY);

    // Trigger accept if swiped far enough
    if (clampedY > trackHeight * 0.9) {
      setIsSwiping(false);
      setSwipeY(trackHeight);
      onAccept();
    }
  };

  const handleTouchEnd = () => {
    if (swipeY < 140) { // If didn't reach top, reset
      setSwipeY(0);
    }
    setIsSwiping(false);
  };

  return (
    <div className="fixed inset-0 z-[6000] bg-slate-950 flex flex-col items-center justify-between py-16 px-8 animate-fadeIn overflow-hidden touch-none">
      {/* Dynamic Background Effects */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200%] h-[200%] bg-indigo-600/10 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, #4f46e5 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
      </div>

      {/* Top Header */}
      <div className="relative z-10 flex flex-col items-center gap-2">
        <div className="bg-indigo-600 px-4 py-1.5 rounded-full flex items-center gap-2 shadow-2xl shadow-indigo-600/40">
          <Zap className="w-3 h-3 text-white fill-white animate-bounce" />
          <span className="text-[10px] font-black text-white uppercase tracking-[0.3em]">Priority Signal Detected</span>
        </div>
        <div className="text-white/40 text-[10px] font-black uppercase tracking-widest mt-2 flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-ping"></span>
          Expires in {timer}s
        </div>
      </div>

      {/* Main Info */}
      <div className="relative z-10 flex flex-col items-center text-center w-full">
        <div className="relative mb-8">
          <div className="absolute -inset-16 bg-indigo-500/10 rounded-full animate-[ping_3s_linear_infinite]"></div>
          <div className="w-32 h-32 bg-slate-900 rounded-[2.5rem] border-4 border-slate-800 shadow-2xl flex items-center justify-center relative z-20 overflow-hidden rotate-3">
            {job.customerAvatar ? (
              <img src={job.customerAvatar} className="w-full h-full object-cover opacity-80" alt="" />
            ) : (
              <User className="w-16 h-16 text-slate-700" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent"></div>
          </div>
          <div className="absolute -bottom-3 -right-3 bg-green-500 text-white p-2.5 rounded-2xl border-4 border-slate-950 shadow-xl z-30">
            <ShieldCheck className="w-5 h-5" />
          </div>
        </div>

        <h3 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.4em] mb-2">Service Request</h3>
        <h2 className="text-4xl font-black text-white tracking-tighter leading-none mb-6">{t[job.skill] || job.skill} Work</h2>

        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 shadow-inner text-left w-full max-w-[280px]">
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-[9px] font-black text-white/30 uppercase tracking-widest mb-1">Customer</p>
              <p className="text-white font-bold text-lg leading-none">{job.customerName || 'Local Client'}</p>
            </div>
            <div className="bg-white/10 px-3 py-1 rounded-full border border-white/20 flex-shrink-0">
              <span className="text-[9px] font-black text-white tracking-widest uppercase">Negotiable</span>
            </div>
          </div>
          <p className="text-white/40 text-[11px] font-medium italic leading-relaxed">
            "{job.description || "Urgent arrival requested for professional inspection."}"
          </p>
        </div>
      </div>

      {/* Premium Swipe Action Area */}
      <div className="w-full max-w-[300px] relative z-20 flex flex-col items-center pb-8">

        {/* Decline Button (Simple Tap) */}
        <button
          onClick={onDecline}
          className="absolute -left-4 bottom-4 w-14 h-14 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center justify-center text-red-500 active:scale-95 transition-all"
        >
          <PhoneOff className="w-5 h-5" />
        </button>

        {/* Swipe Up Track */}
        <div
          ref={swipeRef}
          className="relative w-20 h-40 bg-white/5 rounded-full border border-white/10 flex flex-col items-center justify-end p-2 overflow-hidden cursor-pointer"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={(e) => { setIsSwiping(true); }}
          onMouseMove={(e) => {
            if (!isSwiping || !swipeRef.current) return;
            const trackHeight = 160;
            const clientY = e.clientY;
            const rect = swipeRef.current.getBoundingClientRect();
            const relativeY = rect.bottom - clientY;
            const clampedY = Math.max(0, Math.min(trackHeight, relativeY));
            setSwipeY(clampedY);
            if (clampedY > trackHeight * 0.9) {
              setIsSwiping(false);
              setSwipeY(trackHeight);
              onAccept();
            }
          }}
          onMouseUp={() => {
            if (swipeY < 140) setSwipeY(0);
            setIsSwiping(false);
          }}
          onMouseLeave={() => {
            if (isSwiping) {
              if (swipeY < 140) setSwipeY(0);
              setIsSwiping(false);
            }
          }}
        >
          {/* Animated Arrows Background */}
          {!isSwiping && (
            <div className="absolute top-4 flex flex-col items-center gap-2 opacity-20">
              <ChevronUp className="w-4 h-4 text-white animate-bounce" />
              <ChevronUp className="w-4 h-4 text-white animate-bounce [animation-delay:0.2s]" />
            </div>
          )}

          {/* The Draggable Handle */}
          <div
            className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-[0_10px_30px_rgba(34,197,94,0.4)] relative z-30 transition-transform duration-75"
            style={{
              transform: `translateY(-${swipeY}px)`,
            }}
          >
            <Phone className="w-8 h-8 text-white fill-white" />
          </div>

          {/* Progress Fill */}
          <div
            className="absolute bottom-0 left-0 right-0 bg-green-500/20 w-full rounded-b-full transition-all duration-75"
            style={{ height: `${swipeY + 64}px` }}
          ></div>
        </div>

        <div className="mt-4 flex flex-col items-center">
          <span className="text-[10px] font-black text-white uppercase tracking-[0.4em] animate-pulse">Swipe Up to Accept</span>
          <span className="text-[8px] font-bold text-white/20 uppercase tracking-[0.2em] mt-1">Acceptance is Final</span>
        </div>

      </div>

      <div className="relative z-10 text-white/10 text-[8px] font-black uppercase tracking-[0.6em]">
        End-to-End Encrypted Signal
      </div>
    </div>
  );
};
